/**
 *
 * @todo This helper is overly complex and requires a rewrite ignoring the utc + tz aware date times. The BE requires
 * UTC dates being sent to it, and always returns utc dates. Need to handle this for now.
 *
 *
 */
import moment from "moment-timezone"
import {get, find} from "lodash"

import {ENABLED, DISABLED} from "@irati/insights/constants/schedule"
import {EUROPE_LONDON} from "@irati/insights/constants/timezone"
import {PROPERTY as TEAMS_ID_PROPERTY} from "@irati-web/fields/schedule/preferences/TeamIds"
import {STATUS_CLOSED as SURVEY_STATUS_CLOSED} from "@irati/insights/constants/survey"

/** Initial helper reference */
const helper = {}

/**
 * Internal properties
 *
 * @type {string}
 */
const STATUS_PROPERTY = "status"
const LATEST_SURVEY_PROPERTY = "latestSurvey"
const START_AT_PROPERTY = "startAt"
const LAST_TRIGGERED_AT_PROPERTY = "lastTriggeredAt"
const NEXT_TRIGGER_AT_PROPERTY = "nextTriggerAt"

/**
 *
 * @returns {*}
 */
helper.getUTCNow = () => {
  return moment().utc()
}

/**
 *
 * @returns {*}
 */
helper.getTzAwareNow = () => {
  return moment()
}

/**
 *
 * @param schedule
 * @returns {boolean}
 */
helper.isScheduleEnabled = (schedule) => {
  return get(schedule, STATUS_PROPERTY) === ENABLED
}

/**
 *
 * @param schedule
 * @returns {boolean}
 */
helper.hasSpecifiedTeams = (schedule) => {
  return (get(schedule, TEAMS_ID_PROPERTY, []) || []).length > 0
}

/**
 *
 * @param schedule
 * @returns {boolean}
 */
helper.hasScheduleRunAtLeastOnce = (schedule) => {
  return !!get(schedule, LAST_TRIGGERED_AT_PROPERTY)
}

/**
 * @param schedule
 * @returns {boolean}
 */
helper.isNextTriggerAtInThePast = (schedule) => {
  /** Server time is in utc */
  const nextMoment = helper.tzAwareMoment(schedule, NEXT_TRIGGER_AT_PROPERTY)

  /** If the nextTriggerAt date is invalid default this return to be true */
  if (!nextMoment.isValid()) return true

  return helper.getTzAwareNow().diff(nextMoment) > 0
}

/**
 * n.b. lastTriggeredAt signify's that at least one survey has been triggered. nextTriggerAt is worked out by taking
 * the largest date between startAt and lastTriggeredAt and addingon the periodType + periodValue i.e.
 * periodType = "week", periodValue = 4 would indicate 4 weeks.
 *
 * @param schedule
 * @returns {boolean}
 */
helper.shouldStartAtFieldBeEnabled = (schedule) => {
  return (
    get(schedule, STATUS_PROPERTY) === DISABLED || helper.isNextTriggerAtInThePast(schedule) || !get(schedule, LAST_TRIGGERED_AT_PROPERTY)
  )
}

/**
 *
 * @returns {boolean}
 */
helper.isTzAwareNowBeforeTodaysTriggerTime = (schedule) => {
  /** By default allow a 1 minute buffer before trigger times */
  /** Even if a moment is provided for nowOverride, wrap it anyway to make sure */
  const tzAwareNow = helper.getTzAwareNow()
  const tzAwareNowBuffer = tzAwareNow.clone().add(1, "minute")
  const tzAwareTriggerTimeToday = helper.tzAwareMoment(schedule, NEXT_TRIGGER_AT_PROPERTY)

  return tzAwareNowBuffer.diff(tzAwareTriggerTimeToday) < 0
}

/**
 * Send back today / tomorrow depending on the default trigger time
 *
 * @param schedule
 * @returns {moment}
 */
helper.minTzAwareMomentForStartAtField = (schedule) => {
  /** Use next trigger at date as min date */
  if (!helper.isNextTriggerAtInThePast(schedule) && get(schedule, LAST_TRIGGERED_AT_PROPERTY)) {
    /** @todo remove once tenant has timezone */
    const tzAwareNextTriggerAt = moment(get(schedule, NEXT_TRIGGER_AT_PROPERTY)).tz(EUROPE_LONDON)

    return tzAwareNextTriggerAt.add(1, "days")
  }

  /** Otherwise use tz aware now + default trigger time or tomorrow */
  const tzAwareToday = helper.tzAwareMoment(schedule, NEXT_TRIGGER_AT_PROPERTY)
  const tzAwareTomorrow = tzAwareToday.clone().add("1", "days")

  return helper.isTzAwareNowBeforeTodaysTriggerTime(schedule) ? tzAwareToday : tzAwareTomorrow
}

/**
 *
 * @todo Convert utc date to hardcoded Europe/London timezone until added to tenant
 * @param schedule
 */
helper.tzAwareMoment = (schedule, property) => {
  return moment(get(schedule, property)).tz(EUROPE_LONDON)
}

/**
 *
 * This assumes you are using the platform teams actions
 *
 * @param schedule
 * @param teams
 * @returns {string}
 */
helper.formatScheduleTeamNames = (schedule, teams) => {
  const ids = get(schedule, TEAMS_ID_PROPERTY, []) || []
  const publicNames = ids.map((teamId) => {
    const team = find(teams.list, {uuid: teamId})
    return team ? team.public_name : teamId
  })
  return publicNames.length > 0 ? publicNames.join(", ") : "Everyone"
}

/**
 *
 * Is startAt property further in the future than the nextTriggerAt property.
 * This would occur when a user changes the startAt property before saving the changes to the server.
 *
 * @param schedule
 * @returns {boolean}
 */
helper.isStartAtAfterNextTriggerAt = (schedule) => {
  const startAt = helper.tzAwareMoment(schedule, START_AT_PROPERTY)
  const nextTriggerAt = helper.tzAwareMoment(schedule, NEXT_TRIGGER_AT_PROPERTY)
  return startAt.diff(nextTriggerAt) > 0
}

/**
 * Is the startAt property in the past
 *
 * @param schedule
 * @returns {boolean}
 */
helper.isStartAtInThePast = (schedule) => {
  /** Server time is in utc */
  const startAt = helper.tzAwareMoment(schedule, START_AT_PROPERTY)

  /** If the startAt date is invalid default this return to be true */
  if (!startAt.isValid()) return true

  return helper.getTzAwareNow().diff(startAt) > 0
}

/**
 *
 * @param schedule
 * @returns {boolean}
 */
helper.hasActiveSurvey = (schedule) => {
  const survey = get(schedule, LATEST_SURVEY_PROPERTY)
  return !!survey && survey.status !== SURVEY_STATUS_CLOSED
}

/** export helpers individually */
export const getTzAwareNow = helper.getTzAwareNow
export const getUTCNow = helper.getUTCNow
export const isScheduleEnabled = helper.isScheduleEnabled
export const hasSpecifiedTeams = helper.hasSpecifiedTeams
export const hasScheduleRunAtLeastOnce = helper.hasScheduleRunAtLeastOnce
export const isNextTriggerAtInThePast = helper.isNextTriggerAtInThePast
export const shouldStartAtFieldBeEnabled = helper.shouldStartAtFieldBeEnabled
export const isTzAwareNowBeforeTodaysTriggerTime = helper.isTzAwareNowBeforeTodaysTriggerTime
export const minTzAwareMomentForStartAtField = helper.minTzAwareMomentForStartAtField
export const tzAwareMoment = helper.tzAwareMoment
export const formatScheduleTeamNames = helper.formatScheduleTeamNames
export const isStartAtAfterNextTriggerAt = helper.isStartAtAfterNextTriggerAt
export const isStartAtInThePast = helper.isStartAtInThePast
export const hasActiveSurvey = helper.hasActiveSurvey

/** export default helper obj */
export default helper
